import { useEffect, useRef } from 'react'
import { useToggle } from 'usehooks-ts'
import { Typography } from '@mui/material'
import { InviteLink } from '@weblite/ts-action/hall'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { createNewInviteLink, editInviteLink } from '$logic/hall'
import { closeCreateInviteLink } from '$logic/router'
import { t } from '$i18n'
import { getRoom } from '$logic/room'
import { Switch } from '$common/switch'
import { SettingsSection } from '$common/settings-section'
import { OptionWithTextItem } from '$common/option-with-text-item'
import {
  CreateInviteLinkTimeInput,
  CreateInviteLinkTimeInputApiType,
} from './components/create-invite-link-time-input'
import {
  CreateInviteLinkUsageInput,
  CreateInviteLinkUsageInputApiType,
} from './components/create-invite-link-usage-input'
import { JOIN_HALL_REQUEST_ENABLED } from '$logic/subscription/join-requests'

interface Props {
  roomId: string
  inviteLink?: InviteLink
  isCreation: boolean
}

export function ManageInviteLink({ roomId, inviteLink, isCreation }: Props) {
  const {
    link,
    expiresAt,
    usageLimit,
    needsApproval: needsApprovalDefault,
  } = inviteLink ?? {}

  const [needsApproval, toggleNeedsApproval, setNeedsApproval] =
    useToggle(needsApprovalDefault)

  useEffect(() => {
    setNeedsApproval(Boolean(needsApprovalDefault))
  }, [needsApprovalDefault, setNeedsApproval])

  const timeRef = useRef<CreateInviteLinkTimeInputApiType>(null)
  const usageRef = useRef<CreateInviteLinkUsageInputApiType>(null)

  const onConfirm = async () => {
    const { current: timeApi } = timeRef
    const { current: usageApi } = usageRef
    if (!timeApi || (!usageApi && !needsApproval)) return

    const time = timeApi.getValue()
    const usage = needsApproval ? Infinity : usageApi?.getValue()
    if (isCreation) {
      const room = await getRoom(roomId)
      analite('CHAT_CREATE_LINK', { type: room?.type })
      await createNewInviteLink(roomId, time, usage, needsApproval)
    } else if (link)
      await editInviteLink(roomId, link, time, usage, needsApproval)
    closeCreateInviteLink()
  }

  // const onRevoke = async () => {
  //   // TODO modal
  //   await revokeInviteLink(roomId, link)
  // }

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          onBack={closeCreateInviteLink}
          onConfirm={onConfirm}
          confirmDisabled={false}
          title={
            isCreation
              ? t('inviteLinks.common.createLink')
              : t('inviteLinks.common.editLink')
          }
        />
      }
    >
      {JOIN_HALL_REQUEST_ENABLED && (
        <SettingsSection>
          <OptionWithTextItem
            sx={{ padding: '16px 20px' }}
            primaryText={
              <Typography variant="body2" color="neutral1000">
                {t('inviteLinks.create.needsApproval.title')}
              </Typography>
            }
            secondaryText={
              <Typography variant="caption1" color="neutral500">
                {t('inviteLinks.create.needsApproval.desc')}
              </Typography>
            }
            icon={<Switch checked={needsApproval} />}
            onClick={toggleNeedsApproval}
          />
        </SettingsSection>
      )}

      <CreateInviteLinkTimeInput
        ref={timeRef}
        expiresAt={expiresAt ? new Date(expiresAt) : undefined}
      />
      {!needsApproval && (
        <CreateInviteLinkUsageInput ref={usageRef} usageLimit={usageLimit} />
      )}
    </PageWithAppBar>
  )
}
